import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { serviceProtocolI18n } from './i18n';

const REQUIRED_VARIABLES = [
  { key: 'transcription', label: '{{transcription}}' },
  { key: 'protocol', label: '{{protocol}}' },
  { key: 'feeling', label: '{{feeling}}' },
  { key: 'crm_label', label: '{{crm_label}}' },
];

const formatDateTime = (value, locale = 'es-CL') => {
  if (!value) return '--';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function PromptVariantViewModal({
  Modal,
  Badge,
  show,
  variant,
  onClose,
}) {
  const { language } = useLanguage();
  const serviceProtocolLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: serviceProtocolI18n });

  useEffect(() => {
    serviceProtocolI18n.changeLanguage(serviceProtocolLanguage);
  }, [serviceProtocolLanguage]);

  const currentLocale = useMemo(() => {
    if (serviceProtocolLanguage === 'enUS') return 'en-US';
    if (serviceProtocolLanguage === 'ptBR') return 'pt-BR';
    return 'es-CL';
  }, [serviceProtocolLanguage]);

  if (!Modal) return null;

  const title = variant?.name || t('service_protocol_prompt_view_title_fallback');

  return (
    <Modal
      title={title}
      show={show}
      onClose={onClose}
      primaryBtnTxt={t('service_protocol_prompt_view_close')}
      primaryBtnAction={onClose}
      secondaryBtnTxt={undefined}
      width={720}
    >
      <div className='p-3'>
        <div className='d-flex flex-wrap gap-3 mb-3'>
          {variant?.version !== undefined && variant?.version !== null && (
            <div>
              <p className='mini font-light mb-0'>
                {t('service_protocol_prompt_view_version')}
              </p>
              <p className='fw-medium mb-0'>
                {Badge ? (
                  <Badge text={`v${variant.version}`} color='info' size='sm' />
                ) : (
                  `v${variant.version}`
                )}
              </p>
            </div>
          )}
          {variant?.createdAt && (
            <div>
              <p className='mini font-light mb-0'>
                {t('service_protocol_prompt_view_created_at')}
              </p>
              <p className='fw-medium mb-0'>
                {formatDateTime(variant.createdAt, currentLocale)}
              </p>
            </div>
          )}
          {(variant?.createdByName || variant?.createdBy) && (
            <div>
              <p className='mini font-light mb-0'>
                {t('service_protocol_prompt_view_created_by')}
              </p>
              <p className='fw-medium mb-0'>
                {variant.createdByName || variant.createdBy}
              </p>
            </div>
          )}
        </div>

        <div className='mb-3'>
          <p className='fw-medium mb-1'>
            {t('service_protocol_prompt_view_required_vars')}
          </p>
          <div className='d-flex flex-wrap gap-1'>
            {REQUIRED_VARIABLES.map((v) =>
              Badge ? (
                <Badge key={v.key} text={v.label} color='primary' size='sm' />
              ) : (
                <span
                  key={v.key}
                  style={{
                    display: 'inline-block',
                    background: 'var(--primary-100, #dbeafe)',
                    color: 'var(--primary-700, #1d4ed8)',
                    borderRadius: 4,
                    padding: '2px 8px',
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                  }}
                >
                  {v.label}
                </span>
              )
            )}
          </div>
        </div>

        <div className='mb-3'>
          <p className='fw-medium mb-1'>{t('service_protocol_prompt_view_custom')}</p>
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e6e8eb',
              borderRadius: 8,
              padding: 12,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            {variant?.customInstructions || (
              <span className='font-light'>
                {t('service_protocol_prompt_view_custom_empty')}
              </span>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
